import { createAsyncThunk } from "@reduxjs/toolkit";
import { ZOOM_LEVELS } from "../../consts/map";
import { OUTER, SECTOR, POSTCODE } from "../../consts/layers";
import {
  lookupTableSelector,
  maxSectorsSelector,
  maxPostcodesSelector,
} from "../depot/selectors";
import {
  isZoomingSelector,
  zoomSelector,
  zoomLevelSelector,
  targetZoomLevelSelector,
  receivedUserInputSelector,
  visiblePostcodes as visiblePostcodesSelector,
} from "./selectors";
import {
  getFitBoundsOptions,
  getNewZoomFromScroll,
  getAdjustedZoom,
} from "../../utils/map";
import { getSiblingsCount } from "../../utils/lookUpTables";
import { isNil } from "lodash";

export const setZoomValue = createAsyncThunk(
  "map/setZoomValue",
  (newZoom) => newZoom
);

export const setZoomFromMouseEvent = createAsyncThunk(
  "map/setZoomFromMouseEvent",
  (e, { dispatch, getState }) => {
    const state = getState();
    const isZooming = isZoomingSelector(state);
    const [zoom] = zoomSelector(state);

    if (isZooming) return;
    const newZoom = getNewZoomFromScroll(e, zoom);
    dispatch(setZoomValue([newZoom]));
  }
);

export const setFitBounds = createAsyncThunk(
  "map/setFitBounds",
  ({ bbox, targetZoomLevel }, { dispatch }) => {
    const fitBoundsOptions = getFitBoundsOptions({
      bbox,
      targetZoomLevel,
    });
    dispatch(setReceivedUserInput(false));
    return {
      bbox,
      targetZoomLevel,
      fitBoundsOptions,
    };
  }
);

export const setJumpTo = createAsyncThunk(
  "map/setJumpTo",
  ({ center, targetZoomLevel = null, zoom = null }, { dispatch }) => {
    dispatch(setReceivedUserInput(false));
    const initialZoom = {
      OUTER: 10,
      SECTOR: 11.5,
      POSTCODE: 13,
    };
    return {
      center,
      zoom: !isNil(zoom) ? zoom : initialZoom[targetZoomLevel],
      targetZoomLevel,
    };
  }
);

export const setReceivedUserInput = createAsyncThunk(
  "map/setReceivedUserInput",
  (receivedUserInput) => receivedUserInput
);

export const setTargetZoomLevel = createAsyncThunk(
  "map/setTargetZoomLevel",
  (targetZoomLevel) => targetZoomLevel
);

export const adjustToTargetZoomLevel = createAsyncThunk(
  "map/adjustToTargetZoomLevel",
  (map, { dispatch, getState }) => {
    const state = getState();
    const [zoom] = zoomSelector(state);
    const zoomLevel = zoomLevelSelector(state);
    const targetZoomLevel = targetZoomLevelSelector(state);
    const receivedUserInput = receivedUserInputSelector(state);

    if (receivedUserInput)
      targetZoomLevel && dispatch(setTargetZoomLevel(null));
    else if (targetZoomLevel && targetZoomLevel !== zoomLevel)
      map.zoomTo(getAdjustedZoom({ zoom, zoomLevel, targetZoomLevel }));
  }
);

export const calculateZoomLevel = createAsyncThunk(
  "map/calculateZoomLevel",
  (params, { getState }) => {
    const state = getState();
    const lookupTable = lookupTableSelector(state);
    const maxSectors = maxSectorsSelector(state);
    const maxPostcodes = maxPostcodesSelector(state);
    const visiblePostcodes = visiblePostcodesSelector(state);
    const [zoom] = zoomSelector(state);
    const zoomLevel = zoomLevelSelector(state);

    if (!lookupTable || !visiblePostcodes?.length || zoom < ZOOM_LEVELS.OUTER)
      return OUTER;
    const childrenCount = visiblePostcodes.reduce(
      (acc, postcode) => (acc += lookupTable[zoomLevel][postcode].children),
      0
    );

    if (zoom < ZOOM_LEVELS.SECTOR && zoomLevel === SECTOR) {
      return getSiblingsCount(visiblePostcodes, lookupTable) <= maxSectors * 1.1
        ? SECTOR
        : OUTER;
    } else if (zoom < ZOOM_LEVELS.SECTOR) {
      return childrenCount <= maxSectors ? SECTOR : OUTER;
    } else if (zoomLevel === OUTER) {
      return SECTOR;
    } else if (zoomLevel === POSTCODE) {
      return getSiblingsCount(visiblePostcodes, lookupTable) <=
        maxPostcodes * 1.1
        ? POSTCODE
        : SECTOR;
    } else {
      return childrenCount <= maxPostcodes ? POSTCODE : SECTOR;
    }
  }
);
